import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import planoService, { type PlanoTrabalhoAnexo, type PlanoWorkflowPayload } from '../services/planoTrabalhoService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import { useAuth } from '../context/AuthContext';
import DashboardPagination from '../components/DashboardPagination';
import { getMinhasInstituicoesDetalhadas } from '../services/userService';
import type { InstituicaoDetalhada } from '../types/user.types';
import institutionService from '../services/institutionService';
import { UI_THEME } from '../ui/theme';
import AuthenticatedFileButton from '../components/AuthenticatedFileButton';
import { FIELD_BASE_CLASS, FIELD_CLASS } from '../components/ui/fieldStyles';
import { buttonClassName } from '../components/ui/buttonStyles';
import Button from '../components/ui/Button';
import { Table, Tbody, Td, Th, Thead, Tr } from '../components/ui/Table';
import { toUpperText } from '../utils/formatters';

type WorkflowAction = 'aprovar' | 'devolver' | 'reprovar';

const STATUS_LABEL: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  AGUARDANDO_ASSINATURA: 'Aguardando assinatura',
  EM_ANALISE: 'Em analise',
  APROVADO: 'Aprovado',
  DEVOLVIDO_CORRECAO: 'Devolvido para correcao',
  REPROVADO: 'Reprovado',
  CONCLUIDO: 'Concluido',
};

const STATUS_COLOR: Record<string, string> = {
  RASCUNHO: 'border-line bg-surface-muted text-fg',
  AGUARDANDO_ASSINATURA: 'border-info-line bg-info-soft text-info-fg',
  EM_ANALISE: 'border-info-line bg-info-soft text-info-fg',
  APROVADO: 'border-success-line bg-success-soft text-success-text',
  DEVOLVIDO_CORRECAO: 'border-warning-line bg-warning-soft text-warning-text',
  REPROVADO: 'border-danger-line bg-danger-soft text-danger-text',
  CONCLUIDO: 'border-success-line bg-success-soft text-success-text',
};

const ETAPA_LABEL: Record<string, string> = {
  ORCAMENTO: 'Orçamento',
  CONVENIOS_ANALISE: 'Convênios - análise inicial',
  GESTOR_PARCERIA: 'Gestor da parceria',
  COMISSAO_SELECAO: 'Comissao de selecao',
  CONVENIOS_MINUTA: 'Convenios - minuta do termo de fomento',
  PROCURADORIA_JURIDICA: 'Procuradoria Geral Municipal',
  CONVENIOS_FORMALIZACAO: 'Convenios - formalizacao do termo de fomento',
  CONCLUIDO: 'Concluido',
};

const MODALIDADE_LABEL: Record<string, string> = {
  TERMO_COLABORACAO: 'Termo de colaboracao',
  TERMO_FOMENTO: 'Termo de fomento',
};

const DEFAULT_ROLE_CONFIG = {
  title: 'Fila de planos de trabalho',
  subtitle: 'Acompanhe as etapas disponiveis para o seu perfil.',
  etapas: [],
};

// Gestor da Parceria: atribuicao booleana sobre SECRETARIA, nao role proprio
// (ver User.gestorParceria).
const GESTOR_CONFIG = {
  title: 'Fila do gestor da parceria',
  subtitle: 'Analise os planos da sua secretaria com parecer técnico obrigatório e decisão de aprovar ou reprovar.',
  etapas: ['GESTOR_PARCERIA'],
};

const ETAPA_COMISSAO = 'COMISSAO_SELECAO';
const TIPO_ATA_COMISSAO = 'ATA_COMISSAO';

const ROLE_CONFIG: Record<string, { title: string; subtitle: string; etapas: string[] }> = {
  ADMIN: {
    title: 'Fila geral de aprovacao',
    subtitle: 'Visualize todas as etapas abertas do workflow do plano de trabalho.',
    etapas: ['CONVENIOS_ANALISE', 'GESTOR_PARCERIA', 'CONVENIOS_MINUTA', 'PROCURADORIA_JURIDICA', 'CONVENIOS_FORMALIZACAO'],
  },
  COMISSAO_SELECAO: {
    title: 'Fila da comissao de selecao',
    subtitle: 'Acompanhe os planos aguardando decisão da comissão, com ata obrigatória para aprovação.',
    etapas: [],
  },
  SECRETARIA: {
    title: 'Fila da comissao de selecao',
    subtitle: 'Acompanhe os planos da comissao quando o seu usuario estiver vinculado como membro.',
    etapas: [],
  },
  ANALISTA: {
    title: 'Fila da comissao de selecao',
    subtitle: 'Acompanhe os planos da comissao quando o seu usuario estiver vinculado como membro.',
    etapas: [],
  },
  ORCAMENTO: {
    title: 'Fila do orçamento',
    subtitle: 'Revise os planos devolvidos pela comissão e reenviados para uma nova rodada de pareceres quando estiverem prontos.',
    etapas: ['ORCAMENTO'],
  },
  JURIDICO: {
    title: 'Fila da Procuradoria Geral Municipal',
    subtitle: 'Acompanhe os planos aguardando parecer jurídico e decisão da etapa.',
    etapas: ['PROCURADORIA_JURIDICA'],
  },
  GESTOR_JURIDICO: {
    title: 'Fila da Procuradoria Geral Municipal',
    subtitle: 'Acompanhe os planos aguardando parecer jurídico e decisão da etapa.',
    etapas: ['PROCURADORIA_JURIDICA'],
  },
  CONVENIOS: {
    title: 'Fila de convenios',
    subtitle: 'Acompanhe a analise inicial, a minuta do termo de fomento e a formalizacao final do termo de fomento.',
    etapas: ['CONVENIOS_ANALISE', 'CONVENIOS_MINUTA', 'CONVENIOS_FORMALIZACAO'],
  },
  CAMARA: {
    title: 'Consulta de planos de trabalho',
    subtitle: 'Seu perfil possui acesso somente leitura nesta area.',
    etapas: [],
  },
  VEREADOR: {
    title: 'Consulta de planos de trabalho',
    subtitle: 'Seu perfil possui acesso somente leitura nesta area.',
    etapas: [],
  },
};

const PlanosGestaoPage: React.FC = () => {
  const PAGE_SIZE_OPTIONS = [10, 20, 50];
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const role = String(user?.role || '').toUpperCase();
  const isGestor = Boolean(user?.gestorParceria);
  const roleConfig = isGestor
    ? GESTOR_CONFIG
    : ROLE_CONFIG[role] || DEFAULT_ROLE_CONFIG;
  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const subtleSurfaceClass = isSaas
    ? 'border-teal-100 bg-teal-50/60 dark:border-teal-900/60 dark:bg-teal-950/30'
    : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const primaryButtonClass = buttonClassName({ variant: 'primary' });
  const secondaryButtonClass = buttonClassName({ variant: 'secondary' });

  const [planos, setPlanos] = useState<PlanoTrabalho[]>([]);
  const [instituicoes, setInstituicoes] = useState<InstituicaoDetalhada[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState<'todos' | 'fila' | 'abertos' | 'historico'>(
    role === 'ADMIN' || isGestor ? 'todos' : 'fila'
  );

  const filterStatus = searchParams.get('status') || '';
  const filterModalidade = searchParams.get('modalidade') || '';
  const filterEtapa = searchParams.get('etapaAtual') || '';
  const [query, setQuery] = useState('');
  const hasActiveFilters = Boolean(filterStatus || filterModalidade || filterEtapa);
  const [selectedPlano, setSelectedPlano] = useState<PlanoTrabalho | null>(null);
  const [workflowAction, setWorkflowAction] = useState<WorkflowAction | null>(null);
  const [motivo, setMotivo] = useState('');
  const [workflowParecer, setWorkflowParecer] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [comissaoAtaAnexos, setComissaoAtaAnexos] = useState<PlanoTrabalhoAnexo[]>([]);
  const [ataSelecionada, setAtaSelecionada] = useState<File | null>(null);
  const [loadingAtaAnexos, setLoadingAtaAnexos] = useState(false);
  const [uploadingAta, setUploadingAta] = useState(false);
  const [removingAtaId, setRemovingAtaId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const isConvenios = role === 'CONVENIOS' || role === 'ADMIN';

  const openAnaliseJuridica = (planoId: string) => {
    navigate(`/dashboard/plano/${planoId}/analise-juridica`);
  };

  const fetchPlanos = async () => {
    try {
      setLoading(true);
      setError('');
      const filters = {
        status: filterStatus || undefined,
        modalidade: filterModalidade || undefined,
        etapaAtual: filterEtapa || undefined,
        minhaFila: false,
      };
      const [data, insts] = await Promise.all([
        planoService.listAll(filters),
        getMinhasInstituicoesDetalhadas().catch(() => [] as InstituicaoDetalhada[]),
      ]);
      const rawPlanos = Array.isArray(data) ? data : [];
      // Defensive: Gestor da Parceria should only see plans explicitly assigned to them
      const planosCarregados = (isGestor && user?.id != null)
        ? rawPlanos.filter(p => Number(p.gestorId) === user.id)
        : rawPlanos;
      const knownIds = new Set(insts.map((instituicao) => instituicao.id).filter(Boolean));
      const missingIds = Array.from(new Set(
        planosCarregados
          .map((plano) => plano.instituicaoId)
          .filter((instituicaoId): instituicaoId is string => Boolean(instituicaoId) && !knownIds.has(instituicaoId))
      ));
      const missingInstituicoes = await Promise.all(
        missingIds.map((instituicaoId) => institutionService.getMinimos(instituicaoId))
      );
      setInstituicoes([
        ...insts,
        ...missingInstituicoes
          .filter((instituicao): instituicao is InstituicaoDetalhada => Boolean(instituicao?.id))
          .filter((instituicao) => !knownIds.has(instituicao.id)),
      ]);
      setPlanos(planosCarregados);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nao foi possivel carregar os planos de trabalho.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanos();
  }, [filterStatus, filterModalidade, filterEtapa]);

  useEffect(() => {
    if (role === 'ADMIN' || isGestor) {
      setActiveFilter('todos');
    }
  }, [role, isGestor]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const planosAbertos = useMemo(
    () => planos.filter(plano => ['EM_ANALISE', 'APROVADO'].includes(plano.status || '') && Boolean(plano.etapaAtual)),
    [planos]
  );

  const minhaFila = useMemo(() => {
    if (role === 'ADMIN') {
      return planosAbertos;
    }
    // Filtra diretamente de todos os planos pela etapa do role, sem restringir
    // por status — planos REPROVADO/DEVOLVIDO_CORRECAO na fase do role também
    // devem aparecer na fila (ex: ORCAMENTO com plano reprovado aguardando ação).
    return planos.filter(plano =>
      Boolean(plano.etapaAtual) && roleConfig.etapas.includes(plano.etapaAtual || '')
    );
  }, [planos, role, roleConfig.etapas]);

  const historicoRecente = useMemo(
    () => planos.filter(plano => ['DEVOLVIDO_CORRECAO', 'REPROVADO', 'CONCLUIDO'].includes(plano.status || '')).slice().sort((a, b) => {
      const aTime = new Date(a.dataAtualizacao || a.aprovadoEm || a.enviadoEm || 0).getTime();
      const bTime = new Date(b.dataAtualizacao || b.aprovadoEm || b.enviadoEm || 0).getTime();
      return bTime - aTime;
    }),
    [planos]
  );

  const planosExibidos = activeFilter === 'todos'
    ? planos
    : activeFilter === 'fila'
      ? minhaFila
      : activeFilter === 'abertos'
        ? planosAbertos
        : historicoRecente;

  const planosFiltradosPorQuery = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return planosExibidos;
    return planosExibidos.filter((p) => {
      const haystack = [
        p.titulo,
        p.instituicaoNome,
        p.emendaCodigo,
        p.numeroProtocolo,
        MODALIDADE_LABEL[p.modalidade || ''] || p.modalidade,
        ETAPA_LABEL[p.etapaAtual || ''] || p.etapaAtual,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [planosExibidos, query]);

  const planosPaginados = useMemo(() => {
    const start = (page - 1) * pageSize;
    return planosFiltradosPorQuery.slice(start, start + pageSize);
  }, [page, pageSize, planosFiltradosPorQuery]);

  useEffect(() => {
    setPage(1);
  }, [activeFilter]);

  const metricas = [
    { label: 'Total de planos', value: planos.length, dot: 'bg-slate-400' },
    { label: 'Na minha fila', value: minhaFila.length, dot: isSaas ? 'bg-teal-500' : 'bg-blue-600' },
    { label: 'Fluxos abertos', value: planosAbertos.length, dot: isSaas ? 'bg-indigo-500' : 'bg-violet-500' },
    { label: 'Devolvidos ou reprovados', value: planos.filter(p => ['DEVOLVIDO_CORRECAO', 'REPROVADO'].includes(p.status || '')).length, dot: 'bg-amber-500' },
  ];

  const instituicaoNomePorId = useMemo(
    () => instituicoes.reduce<Record<string, string>>((acc, instituicao) => {
      if (!instituicao.id) return acc;
      acc[instituicao.id] = instituicao.razaoSocial || instituicao.nomeFantasia || instituicao.id;
      return acc;
    }, {}),
    [instituicoes]
  );

  const openActionModal = (plano: PlanoTrabalho, action: WorkflowAction) => {
    setSelectedPlano(plano);
    setWorkflowAction(action);
    setMotivo('');
    setWorkflowParecer(plano.etapaAtual === 'PROCURADORIA_JURIDICA' ? (plano.parecerJuridico || '') : (plano.parecerTecnico || ''));
  };

  const closeActionModal = () => {
    setSelectedPlano(null);
    setWorkflowAction(null);
    setMotivo('');
    setWorkflowParecer('');
    setAtaSelecionada(null);
    setComissaoAtaAnexos([]);
  };

  const currentStageShowsParecer = selectedPlano?.etapaAtual === 'GESTOR_PARCERIA' || selectedPlano?.etapaAtual === 'PROCURADORIA_JURIDICA';
  const currentStageRequiresParecer = selectedPlano?.etapaAtual === 'GESTOR_PARCERIA';
  const currentActionRequiresMotivo = workflowAction === 'devolver' || workflowAction === 'reprovar';
  const currentStageRequiresAta = selectedPlano?.etapaAtual === ETAPA_COMISSAO;
  const hasAtaComissao = comissaoAtaAnexos.length > 0;

  const carregarAtaDaComissao = async (planoId: string) => {
    try {
      setLoadingAtaAnexos(true);
      const anexos = await planoService.listAnexos(planoId);
      setComissaoAtaAnexos(
        anexos.filter(anexo => anexo.etapa === ETAPA_COMISSAO && anexo.tipoDocumento === TIPO_ATA_COMISSAO)
      );
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.error || 'Nao foi possivel carregar a ata da comissao.' });
      setComissaoAtaAnexos([]);
    } finally {
      setLoadingAtaAnexos(false);
    }
  };

  useEffect(() => {
    if (!workflowAction || !selectedPlano || selectedPlano.etapaAtual !== ETAPA_COMISSAO) {
      setComissaoAtaAnexos([]);
      setAtaSelecionada(null);
      return;
    }
    carregarAtaDaComissao(selectedPlano.id);
  }, [workflowAction, selectedPlano?.id, selectedPlano?.etapaAtual]);

  const handleUploadAta = async () => {
    if (!selectedPlano || !ataSelecionada) return;

    try {
      setUploadingAta(true);
      await planoService.uploadAnexo(selectedPlano.id, {
        file: ataSelecionada,
        etapa: ETAPA_COMISSAO,
        tipoDocumento: TIPO_ATA_COMISSAO,
        descricao: 'Ata assinada da comissao de selecao',
      });
      setAtaSelecionada(null);
      await carregarAtaDaComissao(selectedPlano.id);
      setToast({ type: 'success', message: 'Ata assinada anexada com sucesso.' });
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.error || 'Nao foi possivel anexar a ata assinada.' });
    } finally {
      setUploadingAta(false);
    }
  };

  const handleDeleteAta = async (anexoId: string) => {
    if (!selectedPlano) return;

    try {
      setRemovingAtaId(anexoId);
      await planoService.deleteAnexo(selectedPlano.id, anexoId);
      await carregarAtaDaComissao(selectedPlano.id);
      setToast({ type: 'success', message: 'Ata removida com sucesso.' });
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.error || 'Nao foi possivel remover a ata.' });
    } finally {
      setRemovingAtaId(null);
    }
  };

  const handleWorkflowSubmit = async () => {
    if (!selectedPlano || !workflowAction) return;

    if (currentActionRequiresMotivo && !motivo.trim()) {
      setToast({ type: 'error', message: 'Informe o motivo para concluir essa decisao.' });
      return;
    }
    if (currentStageRequiresParecer && !workflowParecer.trim()) {
      setToast({ type: 'error', message: 'O parecer técnico é obrigatório nesta etapa.' });
      return;
    }
    if (currentStageRequiresAta && workflowAction === 'aprovar' && !hasAtaComissao) {
      setToast({ type: 'error', message: 'Anexe a ata assinada da comissao antes de confirmar o parecer.' });
      return;
    }

    const payload: PlanoWorkflowPayload = {
      motivo: motivo.trim() || undefined,
      parecerTecnico: selectedPlano.etapaAtual === 'GESTOR_PARCERIA' ? workflowParecer.trim() || undefined : undefined,
      parecerJuridico: selectedPlano.etapaAtual === 'PROCURADORIA_JURIDICA' ? workflowParecer.trim() || undefined : undefined,
    };

    try {
      setSaving(true);

      if (workflowAction === 'aprovar') {
        await planoService.aprovar(selectedPlano.id, payload);
      } else if (workflowAction === 'devolver') {
        await planoService.devolver(selectedPlano.id, payload);
      } else {
        await planoService.reprovar(selectedPlano.id, payload);
      }

      closeActionModal();
      await fetchPlanos();
      setToast({ type: 'success', message: 'Decisao registrada com sucesso.' });
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.error || 'Nao foi possivel atualizar o workflow do plano.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      <div className={`rounded-3xl border ${heroClass}`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
              {isSaas ? 'Workflow' : 'Gestao'}
            </p>
            <div>
              <h1 className={`text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>
                {isSaas ? 'Gestao ativa de planos' : 'Planos de trabalho'}
              </h1>
              <p className={`mt-2 max-w-3xl text-sm leading-6 ${bodyTextClass}`}>{roleConfig.title}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={fetchPlanos}
              className={`${secondaryButtonClass}`}
            >
              Atualizar fila
            </button>
          </div>
        </div>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4 dark:divide-slate-700">
          {metricas.map(metrica => (
            <div key={metrica.label} className="min-h-[72px] px-4 py-3">
              <p className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${metrica.dot}`} />
                {metrica.label}
              </p>
              <p className={`mt-2 text-2xl font-bold ${strongTextClass}`}>{metrica.value}</p>
            </div>
          ))}
          </div>
        </div>
      </div>

      <div className={`rounded-2xl border p-2 ${surfaceClass}`}>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'todos', title: 'Todos os planos', description: 'Inclui rascunhos, em análise e concluídos.' },
            {
              key: 'fila',
              title: 'Minha fila',
              description: 'Planos aguardando decisao do seu setor.',
            },
            { key: 'abertos', title: 'Fluxos abertos', description: 'Todas as etapas ainda em andamento.' },
            { key: 'historico', title: 'Historico recente', description: 'Planos devolvidos, reprovados ou concluidos.' },
          ].map(item => {
            const active = activeFilter === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setActiveFilter(item.key as 'todos' | 'fila' | 'abertos' | 'historico')}
                className={`rounded-xl border px-3 py-2 text-left text-sm font-semibold transition-colors ${
                  active
                    ? (isSaas ? 'border-teal-200 bg-teal-50 text-teal-900 shadow-sm dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-100' : 'border-blue-200 bg-blue-50 text-blue-900 shadow-sm dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-100')
                    : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200 dark:hover:bg-slate-800'
                }`}
              >
                {item.title}
              </button>
            );
          })}
        </div>
      </div>

      <div className={`rounded-3xl border p-4 ${surfaceClass}`}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className={`text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>Buscar</label>
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              placeholder="Título, instituição, protocolo, emenda..."
              className={`${FIELD_BASE_CLASS} w-72`}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={`text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>Status</label>
            <select
              value={filterStatus}
              onChange={e => {
                const params = new URLSearchParams(searchParams);
                if (e.target.value) params.set('status', e.target.value); else params.delete('status');
                setSearchParams(params);
                setPage(1);
              }}
              className={FIELD_BASE_CLASS}
            >
              <option value="">Todos</option>
              {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className={`text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>Modalidade</label>
            <select
              value={filterModalidade}
              onChange={e => {
                const params = new URLSearchParams(searchParams);
                if (e.target.value) params.set('modalidade', e.target.value); else params.delete('modalidade');
                setSearchParams(params);
                setPage(1);
              }}
              className={FIELD_BASE_CLASS}
            >
              <option value="">Todas</option>
              {Object.entries(MODALIDADE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className={`text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>Etapa atual</label>
            <select
              value={filterEtapa}
              onChange={e => {
                const params = new URLSearchParams(searchParams);
                if (e.target.value) params.set('etapaAtual', e.target.value); else params.delete('etapaAtual');
                setSearchParams(params);
                setPage(1);
              }}
              className={FIELD_BASE_CLASS}
            >
              <option value="">Todas</option>
              {Object.entries(ETAPA_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {(hasActiveFilters || query) && (
            <button
              type="button"
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.delete('status');
                params.delete('modalidade');
                params.delete('etapaAtual');
                setSearchParams(params);
                setQuery('');
                setPage(1);
              }}
              className={`${secondaryButtonClass}`}
            >
              Limpar filtros
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className={`rounded-3xl border p-12 text-center ${surfaceClass} ${labelTextClass}`}>
          Carregando fila de planos...
        </div>
      ) : error ? (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center shadow-sm dark:border-red-800 dark:bg-red-950/30">
          <p className="font-semibold text-danger-text">Erro ao carregar planos</p>
          <p className="mt-2 text-sm text-danger-text">{error}</p>
        </div>
      ) : planosFiltradosPorQuery.length === 0 ? (
        <div className={`rounded-3xl border p-12 text-center ${surfaceClass}`}>
          <p className={`text-lg font-semibold ${strongTextClass}`}>Nenhum plano encontrado nesta visao.</p>
          <p className={`mt-2 text-sm ${labelTextClass}`}>
            {hasActiveFilters
              ? 'Nenhum plano corresponde aos filtros selecionados. Tente alterar ou limpar os filtros.'
              : 'Quando novos planos entrarem na etapa do seu setor, eles aparecerao aqui.'}
          </p>
        </div>
      ) : (
        <div className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
          <Table density="comfortable">
            <Thead>
              <Tr className="border-b border-slate-200 dark:border-slate-700">
                <Th>Plano</Th>
                <Th>Etapa</Th>
                <Th>Status</Th>
                <Th>Atualizacao</Th>
                <Th align="right">Acoes</Th>
              </Tr>
            </Thead>
            <Tbody>
              {planosPaginados.map(plano => {
                const status = plano.status || 'RASCUNHO';
                const etapaOrcamento = plano.etapaAtual === 'ORCAMENTO';
                const podeDecidir = Boolean(plano.etapaAtual)
                  && (role === 'ADMIN' || roleConfig.etapas.includes(plano.etapaAtual || ''))
                  && (['EM_ANALISE', 'APROVADO'].includes(status)
                    || (etapaOrcamento && (status === 'DEVOLVIDO_CORRECAO' || status === 'REPROVADO')));
                const updatedAt = plano.dataAtualizacao || plano.aprovadoEm || plano.enviadoEm || plano.dataCriacao;
                const instituicaoNome = plano.instituicaoId
                  ? instituicaoNomePorId[plano.instituicaoId] || 'Nao informada'
                  : 'Nao informada';

                return (
                  <Tr key={plano.id} className="border-b border-slate-100 last:border-b-0 dark:border-slate-800">
                    <Td>
                      <div className="space-y-1">
                        <p className={`font-semibold ${strongTextClass}`}>{plano.titulo}</p>
                        <p className={`text-xs ${labelTextClass}`}>Emenda: {plano.emendaCodigo || 'Nao informada'}</p>
                        <p className={`text-xs ${labelTextClass}`}>
                          Instituicao: <span className={`font-medium ${bodyTextClass}`}>{instituicaoNome}</span>
                        </p>
                        {isConvenios && plano.etapaAtual === 'CONVENIOS_ANALISE' && (
                          <p className={`text-xs ${labelTextClass}`}>
                            Gestor: <span className={plano.gestorNome ? `font-medium ${bodyTextClass}` : `italic ${labelTextClass}`}>{plano.gestorNome || 'Nao vinculado'}</span>
                          </p>
                        )}
                        {plano.analiseIniciadaPor && (
                          <p className={`text-xs ${labelTextClass}`}>
                            Analise iniciada por:{' '}
                            <span className={`font-medium ${bodyTextClass}`}>{plano.analiseIniciadaPor}</span>
                            {plano.analiseIniciadaEm && (
                              <span className={labelTextClass}> em {new Date(plano.analiseIniciadaEm).toLocaleString('pt-BR')}</span>
                            )}
                          </p>
                        )}
                        {plano.motivoPendencia && (
                          <p className="text-xs text-warning-text">Pendencia: {plano.motivoPendencia}</p>
                        )}
                        {plano.totalMembros != null && plano.totalMembros > 0 && (
                          <span className="inline-flex items-center rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
                            {plano.pareceresConcluidos ?? 0}/{plano.totalMembros} pareceres
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${isSaas ? 'border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300' : 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300'}`}>
                        {ETAPA_LABEL[plano.etapaAtual || ''] || 'Sem etapa ativa'}
                      </span>
                    </Td>
                    <Td>
                      <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_COLOR[status] || STATUS_COLOR.RASCUNHO}`}>
                        {STATUS_LABEL[status] || status}
                      </span>
                    </Td>
                    <Td className={`text-xs ${labelTextClass}`}>
                      {updatedAt ? new Date(updatedAt).toLocaleDateString('pt-BR') : 'Sem data'}
                    </Td>
                    <Td>
                      <div className="flex flex-wrap justify-end gap-2">
                        {plano.etapaAtual === 'PROCURADORIA_JURIDICA' && (
                          <button
                            type="button"
                            onClick={() => openAnaliseJuridica(plano.id)}
                            className={`${buttonClassName({ variant: 'primary', size: 'sm' })}`}
                          >
                            {plano.parecerJuridico?.trim() ? 'Continuar analise juridica' : 'Abrir analise juridica'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => navigate(`/dashboard/plano/full/${plano.id}`)}
                          className={`${buttonClassName({ variant: 'secondary', size: 'sm' })}`}
                        >
                          Ver plano
                        </button>
                        {podeDecidir && (
                          <>
                            {/* Aprovar/Reprovar foram removidos da lista de gestão:
                                a decisão passa a ser feita dentro do plano/workspace.
                                Mantidos apenas o reenvio do Orçamento e a devolução de Convênios. */}
                            {etapaOrcamento && (
                              <Button variant="success" size="sm" onClick={() => openActionModal(plano, 'aprovar')}>
                                Reenviar
                              </Button>
                            )}
                            {plano.etapaAtual === 'CONVENIOS_ANALISE' && (
                              <Button variant="warning-outline" size="sm" onClick={() => openActionModal(plano, 'devolver')}>
                                Devolver
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
          <DashboardPagination
            page={page}
            pageSize={pageSize}
            totalItems={planosFiltradosPorQuery.length}
            onPageChange={setPage}
            onPageSizeChange={(value) => {
              setPageSize(value);
              setPage(1);
            }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            itemLabel="planos"
          />
        </div>
      )}

      {workflowAction && selectedPlano && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/40" onClick={closeActionModal} />
          <div className="relative z-10 w-full max-w-2xl rounded-3xl bg-white p-6 shadow-xl dark:bg-slate-900">
            <div className="space-y-2">
              <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${labelTextClass}`}>Decisao de workflow</p>
              <h2 className={`text-xl font-bold ${strongTextClass}`}>
                {workflowAction === 'aprovar' && (selectedPlano.etapaAtual === 'ORCAMENTO' ? 'Reenviar plano' : 'Aprovar plano')}
                {workflowAction === 'devolver' && 'Devolver para correcao'}
                {workflowAction === 'reprovar' && 'Reprovar plano'}
              </h2>
              <p className={`text-sm ${bodyTextClass}`}>
                Plano: <strong>{selectedPlano.titulo}</strong> · Etapa atual: <strong>{ETAPA_LABEL[selectedPlano.etapaAtual || ''] || 'Sem etapa'}</strong>
              </p>
            </div>

            {currentStageShowsParecer && (
              <div className="mt-5">
                <label className={`mb-1 block text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>
                  {selectedPlano.etapaAtual === 'PROCURADORIA_JURIDICA' ? 'Parecer juridico (opcional)' : 'Parecer tecnico'}
                </label>
                <textarea
                  value={workflowParecer}
                  onChange={e => setWorkflowParecer(toUpperText(e.target.value))}
                  rows={5}
                  className={FIELD_CLASS}
                  placeholder={selectedPlano.etapaAtual === 'PROCURADORIA_JURIDICA'
                    ? 'Opcional: registre o parecer juridico para esta decisao'
                    : 'Registre o parecer técnico obrigatório para esta decisão'}
                />
              </div>
            )}

            {currentActionRequiresMotivo && (
              <div className="mt-5">
                <label className={`mb-1 block text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>Motivo</label>
                <textarea
                  value={motivo}
                  onChange={e => setMotivo(toUpperText(e.target.value))}
                  rows={4}
                  className={FIELD_CLASS}
                  placeholder="Descreva o motivo dessa decisao"
                />
              </div>
            )}

            {currentStageRequiresAta && (
              <div className={`mt-5 rounded-2xl border p-4 ${subtleSurfaceClass}`}>
                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wide ${labelTextClass}`}>Ata assinada da comissao</p>
                    <p className={`mt-1 text-sm ${bodyTextClass}`}>
                      Anexe a ata assinada da reuniao para registrar o parecer desta etapa.
                    </p>
                  </div>
                  {hasAtaComissao && (
                    <span className="inline-flex rounded-full border border-success-line bg-success-soft px-2.5 py-1 text-xs font-semibold text-success-text">
                      Ata anexada
                    </span>
                  )}
                </div>

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    onChange={e => setAtaSelecionada(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-xl file:border-0 file:bg-blue-600 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700 dark:text-slate-300"
                  />
                  <button
                    type="button"
                    onClick={handleUploadAta}
                    disabled={!ataSelecionada || uploadingAta}
                    className={`${primaryButtonClass}`}
                  >
                    {uploadingAta ? 'Enviando...' : 'Anexar ata'}
                  </button>
                </div>

                <div className="mt-4">
                  {loadingAtaAnexos ? (
                    <p className={`text-sm ${labelTextClass}`}>Carregando anexos...</p>
                  ) : hasAtaComissao ? (
                    <div className={`overflow-hidden rounded-2xl border ${surfaceClass}`}>
                      <Table>
                        <Thead>
                          <Tr>
                            <Th>Arquivo</Th>
                            <Th>Enviado em</Th>
                            <Th>Usuario</Th>
                            <Th align="right">Acoes</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {comissaoAtaAnexos.map(anexo => (
                            <Tr key={anexo.id} className="border-t border-line">
                              <Td className="text-fg">{anexo.nomeOriginal}</Td>
                              <Td className="text-fg-muted">
                                {anexo.dataUpload ? new Date(anexo.dataUpload).toLocaleString('pt-BR') : 'Sem data'}
                              </Td>
                              <Td className="text-fg-muted">{anexo.usuarioUpload || 'Nao informado'}</Td>
                              <Td>
                                <div className="flex justify-end gap-2">
                                  {anexo.viewUrl && (
                                    <AuthenticatedFileButton
                                      url={anexo.viewUrl}
                                      fileName={anexo.nomeOriginal}
                                      mimeType={anexo.mimeType}
                                      className="rounded-xl border border-line-strong bg-surface px-3 py-2 text-xs font-medium text-fg hover:bg-surface-muted"
                                    >
                                      Abrir
                                    </AuthenticatedFileButton>
                                  )}
                                  <Button variant="danger-outline" size="sm" onClick={() => handleDeleteAta(anexo.id)} disabled={removingAtaId === anexo.id}>
                                    {removingAtaId === anexo.id ? 'Removendo...' : 'Remover'}
                                  </Button>
                                </div>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-sm text-warning-text">
                      Nenhuma ata assinada anexada ainda. Esse anexo e obrigatorio para confirmar a decisao da comissao.
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeActionModal}
                className={`${secondaryButtonClass}`}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleWorkflowSubmit}
                disabled={saving}
                className={`${primaryButtonClass}`}
              >
                {saving ? 'Salvando...' : 'Confirmar decisao'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className={`rounded-2xl px-4 py-3 text-sm font-medium shadow-lg ${toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanosGestaoPage;
